import { motion } from "framer-motion";
import { Wrench, Zap, Hammer, Paintbrush, Car, Snowflake, Sparkles, Flower2, Cog, MapPin } from "lucide-react";
import { useInView } from "@/hooks";
import { serviceService, cityService } from "@/services/firebase";
import { useState, useEffect } from "react";
import type { Service, City } from "@/types";

const iconMap: Record<string, React.ElementType> = {
  wrench: Wrench,
  zap: Zap,
  hammer: Hammer,
  paintbrush: Paintbrush,
  car: Car,
  snowflake: Snowflake,
  sparkles: Sparkles,
  "flower-2": Flower2,
  Cog: Cog,
  "map-pin": MapPin,
};

export function ServicesSection() {
  const { ref, isInView } = useInView();
  const [services, setServices] = useState<Service[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  useEffect(() => {
    Promise.all([serviceService.getAll().then(setServices), cityService.getAll().then(setCities)]);
  }, []);

  return (
    <section id="services" ref={ref} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-dark mb-4">
            Le bon professionnel, dans votre quartier.
          </h2>
          <p className="text-lg text-gray-500">
            {services.length} métiers, {cities.length} communes d'Abidjan. Et bientôt partout en Côte d'Ivoire.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon] || Wrench;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.05, duration: 0.4 }}
                className="group bg-light rounded-xl p-6 hover:bg-primary hover:shadow-lg transition-all cursor-pointer"
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors"
                  style={{ backgroundColor: `${service.color}15` }}
                >
                  <Icon className="w-6 h-6" style={{ color: service.color }} />
                </div>
                <h3 className="text-lg font-bold text-dark group-hover:text-white transition-colors mb-1">
                  {service.name}
                </h3>
                <p className="text-gray-500 text-sm group-hover:text-white/70 transition-colors">
                  {service.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Cities */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
          className="bg-dark rounded-2xl p-8 md:p-10"
        >
          <h3 className="text-xl font-bold text-white mb-6 text-center">Communes desservies à Abidjan</h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-3">
            {cities.map((city) => {
              const Icon = iconMap[city.icon || "map-pin"] || MapPin;
              return (
                <div
                  key={city.id}
                  className="flex flex-col items-center gap-2 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <Icon className="w-5 h-5 text-primary" />
                  <span className="text-white/80 text-xs font-medium text-center">{city.name}</span>
                </div>
              );
            })}
          </div>
          <p className="text-white/40 text-sm text-center mt-6">
            D'autres villes seront progressivement intégrées : Bouaké, Yamoussoukro, San-Pédro, Daloa...
          </p>
        </motion.div>
      </div>
    </section>
  );
}
